import { h, render, setChildren } from "./core/dom.js";
import { effect, untrack } from "./core/reactive.js";
import { addRoute, initRouter, currentRoute, navigate, setAuthCheck } from "./core/router.js";
import { getToken } from "./api.js";
import { profile, activeSession, pendingRequests, finishingSession, loadProfile, loadMuscles, refreshActiveSession, refreshPendingRequests } from "./store.js";
import * as outbox from "./core/outbox.js";
import { icon } from "./lib/icons.js";
import { fmtDuration, parseDate } from "./lib/format.js";
import { spinner } from "./components/ui.js";
import { maybeShowCookieNotice } from "./components/cookieNotice.js";
import { initNativePushNavigation, ensureNativePushRegistered } from "./lib/nativePush.js";
import { syncNativeRestPreference } from "./lib/notify.js";

import AuthScreen from "./screens/Auth.js";
import Dashboard from "./screens/Dashboard.js";
import Workouts from "./screens/Workouts.js";
import WorkoutEditor from "./screens/WorkoutEditor.js";
import StartScreen from "./screens/Start.js";
import Session from "./screens/Session.js";
import Library from "./screens/Library.js";
import ExerciseEditor from "./screens/ExerciseEditor.js";
import ExerciseDetail from "./screens/ExerciseDetail.js";
import MuscleDetail from "./screens/MuscleDetail.js";
import History from "./screens/History.js";
import SessionDetail from "./screens/SessionDetail.js";
import Week from "./screens/Week.js";
import YearReview from "./screens/YearReview.js";
import Profile from "./screens/Profile.js";
import Achievements from "./screens/Achievements.js";
import Import from "./screens/Import.js";
import Discover from "./screens/Discover.js";
import PublicWorkout from "./screens/PublicWorkout.js";
import PublicProgram from "./screens/PublicProgram.js";
import Friends from "./screens/Friends.js";
import FriendProfile from "./screens/FriendProfile.js";
import Weight from "./screens/Weight.js";
import Measurements from "./screens/Measurements.js";
import ProgressPhotos from "./screens/Progress.js";
import Standards from "./screens/Standards.js";
import Programs from "./screens/Programs.js";
import ProgramEditor from "./screens/ProgramEditor.js";
import Admin from "./screens/Admin.js";
import ResetPassword from "./screens/ResetPassword.js";
import Onboarding from "./screens/Onboarding.js";
import Changelog from "./screens/Changelog.js";
import { Privacy, Terms } from "./screens/Legal.js";

setAuthCheck(() => !!getToken());

addRoute("/login", AuthScreen, { auth: false });
addRoute("/reset", ResetPassword, { auth: false });
addRoute("/welcome", Onboarding, { chrome: false });
addRoute("/privacy", Privacy, { auth: false, chrome: false });
addRoute("/terms", Terms, { auth: false, chrome: false });
addRoute("/", Dashboard);
addRoute("/plan", () => redirectScreen("/workouts"));
addRoute("/workouts", Workouts);
addRoute("/workouts/new", WorkoutEditor);
addRoute("/workouts/:id/edit", WorkoutEditor);
addRoute("/programs", Programs);
addRoute("/programs/new", ProgramEditor);
addRoute("/programs/:id", ProgramEditor);
addRoute("/start", StartScreen);
addRoute("/session", Session);
addRoute("/library", Library);
addRoute("/library/new", ExerciseEditor);
addRoute("/library/:id/edit", ExerciseEditor);
addRoute("/exercise/:id", ExerciseDetail);
addRoute("/muscle/:id", MuscleDetail);
addRoute("/history", History);
addRoute("/history/session/:id", SessionDetail);
addRoute("/week", Week);
addRoute("/week/:date", Week);
addRoute("/year", YearReview);
addRoute("/year/:year", YearReview);
addRoute("/profile", Profile);
addRoute("/more", () => redirectScreen("/profile"));
addRoute("/achievements", Achievements);
addRoute("/import", Import);
addRoute("/discover", Discover);
addRoute("/discover/program/:id", PublicProgram);
addRoute("/discover/:id", PublicWorkout);
addRoute("/friends", Friends);
addRoute("/friends/:id", FriendProfile);
addRoute("/weight", Weight);
addRoute("/measurements", Measurements);
addRoute("/progress/photos", ProgressPhotos);
addRoute("/progress", () => redirectScreen("/history"));
addRoute("/standards", Standards);
addRoute("/changelog", Changelog);
addRoute("/admin", Admin);

const NAV = [
  { path: "/", label: "Home", icon: "home" },
  { path: "/workouts", label: "Plan", icon: "list", routes: ["/plan", "/workouts", "/programs", "/library", "/import"] },
  { path: "__start", label: "Start", icon: "plus", center: true },
  { path: "/history", label: "Progress", icon: "chart", routes: ["/progress", "/history", "/weight", "/measurements", "/achievements", "/standards", "/exercise", "/muscle", "/year", "/week"] },
  { path: "/profile", label: "Profile", icon: "user", routes: ["/more", "/profile", "/friends", "/discover", "/admin", "/changelog"] },
];

function redirectScreen(path) {
  const root = h("div", { class: "screen" }, spinner("Opening…"));
  queueMicrotask(() => navigate(path, { replace: true }));
  return root;
}

function navItemActive(item, activePath) {
  if (item.path === "/") return activePath === "/";
  return (item.routes || [item.path]).some((path) => activePath === path || activePath.startsWith(`${path}/`));
}

function bottomNav(activePath) {
  const onStart = () => navigate(activeSession() ? "/session" : "/start");
  return h(
    "nav",
    { class: "bottom-nav", "aria-label": "Main navigation" },
    h("a", { class: "nav-brand", href: "/", "aria-label": "Kashtu home" },
      h("img", { src: "/favicon.svg", alt: "", width: 34, height: 34 }),
      h("span", null, "kashtu", h("small", null, "YOUR TRAINING COMPANION"))),
    ...NAV.map((item) => {
      if (item.center) {
        const active = activePath === "/session" || activePath === "/start";
        return h(
          "button",
          { class: ["nav-btn", "nav-start", active && "active"], onClick: onStart, "aria-label": activeSession() ? "Resume active workout" : "Start a workout" },
          h("span", { class: "start-fab" }, icon(activeSession() ? "play" : "plus", { size: 24, fill: !!activeSession() })),
          h("span", { class: "nav-start-label" }, activeSession() ? "Resume workout" : "Start workout")
        );
      }
      const active = navItemActive(item, activePath);
      const count = item.path === "/profile" ? pendingRequests() : 0;
      return h(
        "button",
        { class: ["nav-btn", active && "active"], onClick: () => navigate(item.path), "aria-label": item.label, "aria-current": active ? "page" : null },
        h("span", { class: "nav-icon-wrap", style: { position: "relative", display: "inline-flex" } },
          icon(item.icon, { size: 22 }),
          count > 0 ? h("span", { class: "nav-badge" }, count > 9 ? "9+" : String(count)) : null
        ),
        h("span", null, item.label)
      );
    })
  );
}

let barInterval = null;
function activeBar(activePath) {
  if (barInterval) {
    clearInterval(barInterval);
    barInterval = null;
  }
  const session = activeSession();
  if (!session || activePath === "/session") return null;

  const start = parseDate(session.started_at)?.getTime() ?? Date.now();
  const timer = h("span", { class: "ab-timer" }, fmtDuration(Math.floor((Date.now() - start) / 1000)));
  barInterval = setInterval(() => {
    timer.textContent = fmtDuration(Math.floor((Date.now() - start) / 1000));
  }, 1000);

  let totalWorkingSets = 0;
  let doneWorkingSets = 0;
  if (Array.isArray(session.exercises)) {
    for (const ex of session.exercises) {
      if (Array.isArray(ex.sets)) {
        for (const s of ex.sets) {
          if (!s.is_warmup) {
            totalWorkingSets++;
            if (s.is_completed) doneWorkingSets++;
          }
        }
      }
    }
  }

  const pct = totalWorkingSets > 0 ? Math.round((doneWorkingSets / totalWorkingSets) * 100) : 0;

  return h(
    "div",
    { class: "active-bar", onClick: () => navigate("/session") },
    totalWorkingSets > 0
      ? h("div", { class: "ab-progress-track" },
          h("div", { class: "ab-progress-fill", style: { width: `${pct}%` } })
        )
      : null,
    h("span", { class: "pulse" }),
    h("div", { class: "ab-info grow" },
      h("span", { class: "ab-name" }, session.name || "Workout in progress"),
      totalWorkingSets > 0
        ? h("span", { class: "ab-progress-text" }, `${doneWorkingSets} of ${totalWorkingSets} sets done (${pct}%)`)
        : null
    ),
    timer,
    icon("forward", { size: 20 })
  );
}

// A workout finished offline: over here, not yet on the server. It occupies the same slot the
// active-workout bar uses (the two can never coexist — the session is dropped locally the moment
// the completion is queued) and stays until the outbox drains, so the app never quietly looks
// like the workout was saved when it wasn't. Tapping is a "try now" for an impatient user.
function finishingBar() {
  const name = finishingSession();
  if (!name) return null;
  return h(
    "div",
    { class: "active-bar finishing-bar", title: "Tap to retry now", onClick: () => outbox.flush() },
    icon("history", { size: 18 }),
    h("span", { class: "ab-name" }, `${name} — sending when you're back online`),
    icon("forward", { size: 20 })
  );
}

const appRoot = document.getElementById("app");
// A persistent three-slot shell. The screen and the chrome (active-workout bar + bottom
// nav) update independently so a chrome change never touches the mounted screen.
const screenSlot = h("div", { class: "app-screen" });
const barSlot = h("div");
const navSlot = h("div");

// The screen effect subscribes ONLY to the route. The screen is built *untracked*, so any
// store signal a screen reads while constructing (e.g. the live logger reading
// activeSession at mount, then writing it on every set via touch()) does NOT subscribe this
// effect — a presence/badge/session update can't tear down the mounted screen. Screens
// manage their own in-place updates.
function renderScreen() {
  const route = currentRoute();
  if (!route) return;
  untrack(() => render(screenSlot, route.route.screen(route.params)));
  requestAnimationFrame(() => {
    if (appRoot.inert) return;
    const heading = screenSlot.querySelector("h1");
    if (heading) { heading.tabIndex = -1; heading.focus({preventScroll:true}); }
  });
}

// The chrome effect DOES track activeSession + pendingRequests so the bar/nav stay live,
// but it only ever rewrites its own slots.
function renderChrome() {
  const route = currentRoute();
  appRoot.dataset.chrome = route?.route.auth && route.route.chrome !== false ? "visible" : "hidden";
  if (!route || !route.route.auth || route.route.chrome === false) {
    setChildren(barSlot);
    setChildren(navSlot);
    return;
  }
  setChildren(barSlot, activeBar(route.path) || finishingBar());
  appRoot.dataset.session = String(route.path === "/session");
  setChildren(navSlot, route.path === "/session" ? null : bottomNav(route.path));
}

async function boot() {
  render(appRoot, h("div", { class: "screen" }, spinner("Starting…")));
  if (getToken()) {
    try {
      await Promise.all([loadProfile(), loadMuscles()]);
      await refreshActiveSession();
      refreshPendingRequests();
    } catch (e) {
      console.error("Boot error", e);
    }
    // Profile is the one load the shell can't run without. If it failed (a 401 already
    // bounced us to /login; otherwise a timeout/offline blip), show a retry instead of
    // mounting a broken shell or — before the fetch timeout existed — hanging on the spinner.
    if (getToken() && !profile()) {
      render(appRoot, h("div", { class: "screen center", style: { paddingTop: "20vh" } },
        h("p", { class: "dim" }, "Couldn't reach the server."),
        h("button", { class: "btn btn-primary mt", onClick: () => boot() }, "Try again")
      ));
      return;
    }
  }
  // A brand-new account that hasn't been through first-run setup yet lands on /welcome
  // (unless it's mid-OAuth-redirect on /login, which Auth.js routes itself).
  if (getToken() && profile() && !profile().onboarded && location.pathname !== "/welcome") {
    history.replaceState({}, "", "/welcome");
  }

  // Mount the persistent shell (clearing the boot spinner), then wire the two effects.
  render(appRoot);
  appRoot.append(screenSlot, barSlot, navSlot);
  effect(renderScreen);
  effect(renderChrome);
  initRouter();

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  }

  // In the Android shell, tapping an FCM notification routes to its target screen, and every
  // launch re-registers the device token so a signed-in phone is always actually reachable.
  initNativePushNavigation((url) => navigate(url));
  syncNativeRestPreference();
  if (getToken()) void ensureNativePushRegistered();

  maybeShowCookieNotice();
}

boot();
